import { ChangeDetectorRef, Component, DestroyRef, ElementRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, filter, take } from 'rxjs';

import { EntrySelectors } from 'src/app/state/entry.selectors';
import { EntryData, Structure } from '../structure-data.models';
import { UtilService } from 'src/app/util.service';
import { CommonService } from 'src/app/common.service';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { MolstarPluginService } from 'src/app/molstar-plugin-service';
import { ConfigService } from 'src/app/config.service';
import { resetColoring, resetTooltips } from 'src/app/molstar-alpha-missense';
import { PaeSelectedRegions } from 'src/app/pae-view/pae-view.component';
import { Chain, PaeDataService } from 'src/app/pae-view/pae-data.service';
import { DownloadOption } from '../entry-dropdown/entry-dropdown.component';

interface MappedMacromolecule {
  tedNumber: number;
  label: string;
  displayTitle: string;
  displayChains: string;
  chainLabels: string[];
  uniprotAccession: string;
  uniprotDescription: string;
  groupedSources: EntryData[];
  source: EntryData;
}

interface BiologicalFunctionInfo {
  text: string;
  source: string;
  source_url: string;
}

const BIOLOGICAL_FUNCTION_WORD_LIMIT = 20;
const SEARCH_UNIPROT_ACCESSION_REGEX =
  /^([OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9](?:[A-Z][A-Z0-9]{2}[0-9]){1,2})(?:-\d+)?$/i;

@Component({
  selector: 'macromolecules-tab',
  templateUrl: './macromolecules-tab.component.html',
  styleUrl: './macromolecules-tab.component.css',
  standalone: false,
})
export class MacromoleculesTabComponent {
  private store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  public accession = toSignal(this.store.select(EntrySelectors.accession));
  public entryData = signal<EntryData>({} as EntryData);
  public complexData = signal<any>({});
  public macromoleculesData = signal<EntryData[]>([]);
  public chainData = signal<Chain[]>([]);
  public activeMacromolecule = signal<EntryData | null>(null);
  // Fragment of a larger UniProt protein (start !== 1) — surfaced next to the UniProt
  // accession in the template (AFPDBE-1445 follow-up).
  public activeMacromoleculeRange = computed(() => this.utilService.getMacromoleculeResidueRange(this.activeMacromolecule()));
  public mappedMacromolecules = signal<MappedMacromolecule[]>([]);

  public isCopySequence = false;
  public pdbeMolstar: any;
  public structure: Structure;
  public lastUpdateDate = '';
  public molLoadComplete = false;
  public readonly isSidebarDisplayed = signal<boolean>(true);
  public kbCount = 0;
  public chainDropdownOptions: DownloadOption[] = [];
  public chainDropdownSelected = '';
  public biologicalFunction = signal<BiologicalFunctionInfo>({
    text: 'Data unavailable (function not known)',
    source: 'tooltip',
    source_url: '',
  });
  public isBiologicalFunctionLoading = signal<boolean>(false);
  public isBiologicalFunctionExpanded = signal<boolean>(false);
  public isAlsoComponentInLoading = signal<boolean>(false);
  public alsoComponentInCount = signal<number | null>(null);
  private biologicalFunctionCache = new Map<string, BiologicalFunctionInfo>();
  private activeBiologicalFunctionAccession = '';
  private alsoComponentInCache = new Map<string, number>();
  private activeAlsoComponentInAccession = '';
  constructor(
    private utilService: UtilService,
    public commonService: CommonService,
    public gaService: GoogleAnalyticsService,
    private configService: ConfigService,
    private el: ElementRef,
    private molstarPluginService: MolstarPluginService,
    private paeDataService: PaeDataService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    effect(() => {
      this.chainData.set(this.paeDataService.chains());
      this.rebuildMacromoleculeCards();
    });

    effect(() => {
      const accession = this.activeMacromolecule()?.uniprotAccession;
      this.loadBiologicalFunction(accession);
      this.loadAlsoComponentInCount(accession);
    });
  }

  ngOnInit(): void {
    this.store.select(EntrySelectors.complexData)
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((complexData) => {
        this.complexData.set(complexData || {});
      });

    combineLatest([
      this.store.select(EntrySelectors.entryData).pipe(),
      this.store.select(EntrySelectors.complexData).pipe(),
      this.store.select(EntrySelectors.macromoleculesData).pipe(),
    ])
      .pipe(
        filter(([entryData, _, macromoleculesData]) =>
          !!entryData &&
          !!entryData.modelEntityId &&
          Array.isArray(macromoleculesData)
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([entryData, complexData, macromoleculesData]) => {
        this.entryData.set(entryData);
        this.complexData.set(complexData || {});
        this.macromoleculesData.set(macromoleculesData || []);
        this.rebuildMacromoleculeCards();
        this.initMolstar();
      });
  }

  getExperimentalStructuresText() {
     //Get structure count in PDBe-KB
    const apiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&rows=0&omitHeader=true&json.nl=map&q=uniprot_accession:${this.activeMacromolecule()?.uniprotAccession}&wt=json`;
    this.commonService.getApiData(apiUrl).subscribe(apiData => {
      if(apiData && apiData.grouped && apiData.grouped.pdb_id) {
        this.kbCount = apiData.grouped.pdb_id.ngroups;
      }
    });
  }

  private rebuildMacromoleculeCards(): void {
    const macromolecules = this.macromoleculesData() || [];
    const chains = this.chainData() || [];

    // uniprotAccession alone isn't a reliable group key: a polyprotein cleaved into separate
    // chains (e.g. NS3/NS5) can report the SAME accession for genuinely different chains with
    // different residue ranges (AFPDBE-1435) — see UtilService.groupMacromoleculeChains.
    const groups = this.utilService.groupMacromoleculeChains(macromolecules);

    const mapped = groups.map((group, index) => {
      // Sort this chain-group's raw rows by chain ID so `representative` (and therefore
      // groupedSources[0], the default activeMacromolecule on first load) is always the
      // alphabetically-first chain — the same ordering chainLabels/the chain dropdown
      // already use below. Without this, `representative` was whatever chain happened to
      // come first in the raw API response, which could be Chain B while the dropdown
      // still defaulted to showing "Chain A", so "Copy sequence" copied the wrong chain's
      // sequence on first load (AFPDBE-1418).
      const sortedGroup = [...group].sort((a, b) =>
        ((a as any)?.chainId || '').localeCompare((b as any)?.chainId || '')
      );
      const representative = sortedGroup[0];
      const chainLabels = this.getChainLabelsForGroup(sortedGroup, chains).sort((a, b) => a.localeCompare(b));
      const titleFromChainData = chains.find(
        (chain) => this.normalizeText(chain.name) === this.normalizeText(representative?.uniprotDescription)
      )?.name;

      return {
        tedNumber: index + 1,
        label: representative?.uniprotAccession || '-',
        displayTitle: representative?.uniprotDescription || titleFromChainData || representative?.uniprotAccession || `Macromolecule ${index + 1}`,
        displayChains: chainLabels.length > 0 ? `Chain ${chainLabels.join(', ')}` : 'Chain data unavailable',
        chainLabels,
        uniprotAccession: representative?.uniprotAccession,
        uniprotDescription: representative?.uniprotDescription,
        groupedSources: sortedGroup,
        source: representative,
      };
    });

    // Sort cards so that Chain A always appears before Chain B, etc.
    mapped.sort((a, b) => {
      const aChain = a.chainLabels[0] || '';
      const bChain = b.chainLabels[0] || '';
      return aChain.localeCompare(bChain);
    });
    // Re-assign tedNumber after sorting
    mapped.forEach((item, index) => { item.tedNumber = index + 1; });

    this.mappedMacromolecules.set(mapped);

    if (mapped.length === 0) {
      this.activeMacromolecule.set(null);
      this.kbCount = 0;
      return;
    }

    const previousAccession = this.activeMacromolecule()?.uniprotAccession;
    const previousDescription = this.activeMacromolecule()?.uniprotDescription;
    const previousChainId = this.getCurrentSelectedChainId();
    // Match by the physically selected chain first — uniprotAccession alone is ambiguous
    // when two cards share an accession (AFPDBE-1435); description is a secondary tiebreaker
    // before giving up and defaulting to the first card.
    const selectedCard =
      mapped.find((item) =>
        item.groupedSources.some((row) => ((row as any)?.chainId || '').trim() === previousChainId)
      ) ||
      mapped.find((item) => item.uniprotAccession === previousAccession && item.uniprotDescription === previousDescription) ||
      mapped[0];
    const selectedSource = this.getSourceForChainInCard(selectedCard, previousChainId) || selectedCard.source;

    this.activeMacromolecule.set(selectedSource);
    this.syncChainDropdownForActiveMacromolecule();
    this.getExperimentalStructuresText();
  }

  private getChainLabelsForGroup(group: EntryData[], chains: Chain[]): string[] {
    const unique = new Set<string>();
    const normalizedNames = new Set(group.map((item) => this.normalizeText(item?.uniprotDescription)).filter(Boolean));

    for (const item of group) {
      const chainIdFromEntry = (item as any)?.chainId as string | undefined;
      if (chainIdFromEntry) {
        unique.add(chainIdFromEntry);
      }
    }

    for (const chain of chains) {
      if (normalizedNames.has(this.normalizeText(chain?.name)) && chain?.label_asym_id) {
        unique.add(chain.label_asym_id);
      }
    }

    if (unique.size === 0 && group.length === 1 && chains.length === this.macromoleculesData().length) {
      const source = group[0];
      const sourceIndex = this.macromoleculesData().findIndex((item) => item === source);
      const chainFromIndex = chains[sourceIndex]?.label_asym_id;
      if (chainFromIndex) {
        unique.add(chainFromIndex);
      }
    }

    return Array.from(unique);
  }

  private normalizeText(text: string | undefined): string {
    return (text || '').trim().toLowerCase();
  }

  async handleSelectedCard(card: MappedMacromolecule): Promise<void> {
    this.activeMacromolecule.set(card.source);
    this.syncChainDropdownForActiveMacromolecule();
    this.getExperimentalStructuresText();

    // For heterodimers (single-chain card), focus selected chain and dim all others.
    if (card.chainLabels.length === 1) {
      await this.applyCardChainSelectionToMolstar(card.chainLabels[0]);
    } else {
      await this.focusFullChain();
    }

    this.changeDetectorRef.detectChanges();
  }

  public showChainDropdown(): boolean {
    const activeCard = this.getActiveMappedMacromolecule();
    return !!activeCard && activeCard.chainLabels.length > 1;
  }

  public getActiveChainTitle(): string {
    const activeCard = this.getActiveMappedMacromolecule();
    if (!activeCard) {
      return 'Chain';
    }

    if (this.showChainDropdown()) {
      return this.chainDropdownSelected || this.chainDropdownOptions[0]?.name || 'Chain';
    }

    if (activeCard.chainLabels.length > 0) {
      return `Chain ${activeCard.chainLabels[0]}`;
    }

    return activeCard.displayChains || 'Chain data unavailable';
  }

  public async onChainDropdownSelect(optionName: string): Promise<void> {
    this.chainDropdownSelected = optionName;
    this.syncActiveMacromoleculeForSelectedChain(optionName);
    await this.applySelectedChainToMolstar(optionName);
    this.getExperimentalStructuresText();
  }

  private syncChainDropdownForActiveMacromolecule(): void {
    const activeCard = this.getActiveMappedMacromolecule();
    const chainLabels = activeCard?.chainLabels || [];
    const previousSelection = this.chainDropdownSelected;

    this.chainDropdownOptions = chainLabels.map((label) => {
      const normalizedLabel = (label || '').trim();
      return {
        name: `Chain ${normalizedLabel}`,
        url: `chain-${normalizedLabel}`,
        downloadable: false,
      };
    });

    const hasPreviousSelection = this.chainDropdownOptions.some((option) => option.name === previousSelection);
    this.chainDropdownSelected = hasPreviousSelection ? previousSelection : this.chainDropdownOptions[0]?.name || '';
  }

  private getActiveMappedMacromolecule(): MappedMacromolecule | undefined {
    const active = this.activeMacromolecule();
    if (!active) {
      return undefined;
    }

    return this.mappedMacromolecules().find((item) =>
      item.source === active ||
      item.groupedSources.includes(active) ||
      // Last-resort fallback: accession alone is ambiguous when two cards share one
      // (AFPDBE-1435), so also require description to match before accepting it.
      (!!active.uniprotAccession &&
        item.uniprotAccession === active.uniprotAccession &&
        item.uniprotDescription === active.uniprotDescription)
    );
  }

  private extractChainIdFromDropdownOption(optionName: string): string {
    return optionName.replace(/^Chain\s+/i, '').trim();
  }

  private syncActiveMacromoleculeForSelectedChain(optionName: string): void {
    const activeCard = this.getActiveMappedMacromolecule();
    const selectedChainId = this.extractChainIdFromDropdownOption(optionName);

    if (!activeCard || !selectedChainId) {
      return;
    }

    const chainSpecificSource = this.getSourceForChainInCard(activeCard, selectedChainId);

    if (chainSpecificSource) {
      this.activeMacromolecule.set(chainSpecificSource);
    }
  }

  private getCurrentSelectedChainId(): string {
    const chainIdFromActive = ((this.activeMacromolecule() as any)?.chainId || '').trim();
    if (chainIdFromActive) {
      return chainIdFromActive;
    }

    return this.extractChainIdFromDropdownOption(this.chainDropdownSelected);
  }

  private getSourceForChainInCard(card: MappedMacromolecule | undefined, chainId: string): EntryData | undefined {
    if (!card || !chainId) {
      return undefined;
    }

    const chainSpecificSource = card.groupedSources.find(
      (item) => ((item as any)?.chainId || '').trim() === chainId
    );

    if (chainSpecificSource) {
      return chainSpecificSource;
    }

    // Fallback for payloads where per-row chainId is absent: align sorted labels to grouped rows.
    const selectedIndex = card.chainLabels.indexOf(chainId);

    if (selectedIndex !== -1 && selectedIndex < card.groupedSources.length) {
      return card.groupedSources[selectedIndex];
    }

    return undefined;
  }

  private async applySelectedChainToMolstar(optionName: string): Promise<void> {
    const activeCard = this.getActiveMappedMacromolecule();
    const selectedChainId = this.extractChainIdFromDropdownOption(optionName);

    if (!this.pdbeMolstar || !activeCard || !selectedChainId) {
      return;
    }

    const uniqueChains = activeCard.chainLabels;
    if (!uniqueChains.includes(selectedChainId)) {
      return;
    }

    const otherChainIds = uniqueChains.filter((id) => id !== selectedChainId);
    const molstarSelection = {
      zoom: [{
        auth_asym_id: selectedChainId,
      }],
      select: {
        data: [...otherChainIds.map((id) => ({ auth_asym_id: id, color: null, opacity: 0.4 }))],
      },
    };

    await this.pdbeMolstar.visual.select(molstarSelection.select);
    await this.pdbeMolstar.visual.focus(molstarSelection.zoom);
  }

  private async applyCardChainSelectionToMolstar(selectedChainId: string): Promise<void> {
    if (!this.pdbeMolstar || !selectedChainId) {
      return;
    }

    const allChainIds = this.getAllKnownChainLabels();
    const otherChainIds = allChainIds.filter((id) => id !== selectedChainId);

    const molstarSelection = {
      zoom: [{
        auth_asym_id: selectedChainId,
      }],
      select: {
        data: [...otherChainIds.map((id) => ({ auth_asym_id: id, color: null, opacity: 0.4 }))],
      },
    };

    await this.pdbeMolstar.visual.select(molstarSelection.select);
    await this.pdbeMolstar.visual.focus(molstarSelection.zoom);
  }

  private getSelectedChainIdForCurrentContext(): string | null {
    if (this.showChainDropdown()) {
      const chainIdFromDropdown = this.extractChainIdFromDropdownOption(this.chainDropdownSelected);
      return chainIdFromDropdown || null;
    }

    const chainIdFromActive = ((this.activeMacromolecule() as any)?.chainId || '').trim();
    if (chainIdFromActive) {
      return chainIdFromActive;
    }

    const activeCard = this.getActiveMappedMacromolecule();
    if (activeCard?.chainLabels?.length) {
      return activeCard.chainLabels[0];
    }

    return null;
  }

  private async applyDefaultChainSelectionOnLoad(): Promise<void> {
    if (!this.pdbeMolstar) {
      return;
    }

    const selectedChainId = this.getSelectedChainIdForCurrentContext();
    if (!selectedChainId) {
      return;
    }

    await this.applyCardChainSelectionToMolstar(selectedChainId);
  }

  private getAllKnownChainLabels(): string[] {
    const unique = new Set<string>();

    for (const card of this.mappedMacromolecules()) {
      for (const label of card.chainLabels) {
        const normalizedLabel = (label || '').trim();
        if (normalizedLabel) {
          unique.add(normalizedLabel);
        }
      }
    }

    if (unique.size === 0) {
      for (const chain of this.chainData()) {
        const label = (chain?.label_asym_id || '').trim();
        if (label) {
          unique.add(label);
        }
      }
    }

    return Array.from(unique);
  }

  public toggleSidebar(): void {
    this.isSidebarDisplayed.update((prev) => !prev);
  }

  public getPlddtLevel(plddt: number): string {
    return plddt > 90 ? 'Very high' :
      plddt > 70 ? 'High' :
        plddt > 50 ? 'Low' :
          'Very low';
  }

  public getipSAELevel(ipSAE: number): string {
    return ipSAE >= 0.8 ? 'Very high' :
      ipSAE >= 0.7 ? 'Confident' :
        ipSAE >= 0.6 ? 'Low' :
          'Very low';
  }

  public hasPlddtDistribution(): boolean {
    const data = this.activeMacromolecule();
    if (!data) {
      return false;
    }

    const values = [
      data.fractionPlddtVeryHigh,
      data.fractionPlddtConfident,
      data.fractionPlddtLow,
      data.fractionPlddtVeryLow,
    ];

    return values.some((value) => typeof value === 'number');
  }

  private loadBiologicalFunction(accession: string | undefined): void {
    if (!accession) {
      this.isBiologicalFunctionLoading.set(false);
      this.isBiologicalFunctionExpanded.set(false);
      this.biologicalFunction.set(this.defaultBiologicalFunction());
      return;
    }

    const cached = this.biologicalFunctionCache.get(accession);
    if (cached) {
      this.isBiologicalFunctionLoading.set(false);
      this.isBiologicalFunctionExpanded.set(false);
      this.biologicalFunction.set(cached);
      return;
    }

    this.activeBiologicalFunctionAccession = accession;
    this.isBiologicalFunctionLoading.set(true);

    const uniprotApiUrl = this.configService.getConfig().uniprotApiUrl + accession;
    this.commonService.getApiData(uniprotApiUrl).subscribe({
      next: (uniprotData) => {
        if (this.activeBiologicalFunctionAccession !== accession) {
          return;
        }

        const parsed = this.parseBiologicalFunction(uniprotData);
        this.biologicalFunctionCache.set(accession, parsed);
        this.biologicalFunction.set(parsed);
        this.isBiologicalFunctionLoading.set(false);
        this.isBiologicalFunctionExpanded.set(false);
      },
      error: () => {
        if (this.activeBiologicalFunctionAccession !== accession) {
          return;
        }

        this.biologicalFunction.set(this.defaultBiologicalFunction());
        this.isBiologicalFunctionLoading.set(false);
        this.isBiologicalFunctionExpanded.set(false);
      },
    });
  }

  public getShortenedBiologicalFunctionText(): string {
    const text = this.biologicalFunction().text;
    if (!text) {
      return '';
    }

    const words = text.split(' ');
    if (words.length <= BIOLOGICAL_FUNCTION_WORD_LIMIT) {
      return text;
    }

    const wordSubset = words.slice(0, BIOLOGICAL_FUNCTION_WORD_LIMIT);
    return `${wordSubset.join(' ')} ...`;
  }

  public canShowMoreBiologicalFunction(): boolean {
    const text = this.biologicalFunction().text;
    if (!text) {
      return false;
    }

    return text.split(' ').length >= BIOLOGICAL_FUNCTION_WORD_LIMIT;
  }

  public toggleBiologicalFunctionText(): void {
    this.isBiologicalFunctionExpanded.update((value) => !value);
  }

  private parseBiologicalFunction(uniprotData: any): BiologicalFunctionInfo {
    const bioFunction: BiologicalFunctionInfo = this.defaultBiologicalFunction();
    let catalyticActivity: string | undefined;

    if (uniprotData && Array.isArray(uniprotData.comments)) {
      const functionComment = uniprotData.comments.find(
        (comment) => comment.type === 'FUNCTION' && comment.text?.length
      );

      if (functionComment) {
        const functionText = functionComment.text?.[0]?.value;
        if (functionText) {
          bioFunction.text = functionText.endsWith('.') ? functionText : `${functionText}.`;
          bioFunction.source = 'UniProt';
          bioFunction.source_url = `https://www.uniprot.org/uniprot/${uniprotData.accession}`;
        }
      }

      for (const comment of uniprotData.comments) {
        if (comment.type === 'CATALYTIC_ACTIVITY') {
          catalyticActivity = comment?.reaction?.name;
        }
      }
    }

    if (bioFunction.text === 'Data unavailable (function not known)' && catalyticActivity !== undefined) {
      bioFunction.text = `Catalytic activity: ${catalyticActivity}`;
    }

    if (bioFunction.text !== 'Data unavailable') {
      const pubmed = /PubMed:([0-9]+)/g;
      bioFunction.text = bioFunction.text.replace(
        pubmed,
        '<a href="https://www.uniprot.org/citations/$1" target="_blank">PubMed:$1</a>'
      );
    }

    return bioFunction;
  }

  private defaultBiologicalFunction(): BiologicalFunctionInfo {
    return {
      text: 'Data unavailable (function not known)',
      source: 'tooltip',
      source_url: '',
    };
  }

  private loadAlsoComponentInCount(accession: string | undefined): void {
    if (!accession) {
      this.isAlsoComponentInLoading.set(false);
      this.alsoComponentInCount.set(null);
      return;
    }

    const cached = this.alsoComponentInCache.get(accession);
    if (cached !== undefined) {
      this.isAlsoComponentInLoading.set(false);
      this.alsoComponentInCount.set(cached);
      return;
    }

    this.activeAlsoComponentInAccession = accession;
    this.isAlsoComponentInLoading.set(true);

    const query = this.buildSearchQueryForAccession(accession);
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : '';
    const searchApiUrl = `${this.configService.getConfig().searchApiUrl}?q=${query}&type=main&start=0&rows=1${auth}`;

    this.commonService.getApiData(searchApiUrl, { isInternal: true }).subscribe({
      next: (apiData) => {
        if (this.activeAlsoComponentInAccession !== accession) {
          return;
        }

        const numFound = typeof apiData?.numFound === 'number' ? apiData.numFound : 0;
        this.alsoComponentInCache.set(accession, numFound);
        this.alsoComponentInCount.set(numFound);
        this.isAlsoComponentInLoading.set(false);
      },
      error: () => {
        if (this.activeAlsoComponentInAccession !== accession) {
          return;
        }

        this.alsoComponentInCount.set(0);
        this.isAlsoComponentInLoading.set(false);
      },
    });
  }

  private buildSearchQueryForAccession(accession: string): string {
    const trimmed = accession.trim();
    const query = SEARCH_UNIPROT_ACCESSION_REGEX.test(trimmed)
      ? `(text:${trimmed}*)`
      : `text:${trimmed}`;
    return encodeURIComponent(query);
  }

  public copySequence(sequence: string): void {
    const text = `${sequence || ''}`;
    this.utilService.copy(text);

    this.isCopySequence = true;
    setTimeout(() => {
      this.isCopySequence = false;
    }, 3000);

    this.gaService.eventEmitter('sequence_copy', 'interaction', 'click', 'copy_sequence', 'Click on Copy sequence');
  }

  public getActiveSequenceForCopy(): string {
    return (
      this.activeMacromolecule()?.sequence ||
      this.activeMacromolecule()?.uniprotSequence ||
      this.entryData()?.sequence ||
      ''
    );
  }

  public onRelatedEntryClick(linkType: string): void {
    this.gaService.emitRelatedEntryClick(linkType, this.entryData()?.isComplex, this.entryData()?.assemblyType);
  }

  private async focusFullChain(): Promise<void> {
    if (!this.pdbeMolstar || !this.entryData()?.sequenceEnd) {
      return;
    }

    await this.pdbeMolstar.visual.focus([
      {
        struct_asym_id: 'A',
        start_residue_number: 1,
        end_residue_number: this.entryData().sequenceEnd,
      },
    ]);
  }

  async clearSeqSelection(): Promise<void> {
    if (this.pdbeMolstar) {
      await this.pdbeMolstar.visual.clearSequenceColor();
    }
  }

  cmSelectionUpdate(cmSelectedRegions: PaeSelectedRegions): void {
    this.handleCmUpdate(cmSelectedRegions);
  }

  async handleCmUpdate(regions: PaeSelectedRegions): Promise<void> {
    if (!this.molLoadComplete) {
      return;
    }

    if (!regions) {
      await this.clearSeqSelection();
      this.applyMolstarColoring();

      const selectedChainId = this.getSelectedChainIdForCurrentContext();
      if (selectedChainId) {
        await this.applyCardChainSelectionToMolstar(selectedChainId);
      }

      return;
    }

    const proteinChains = this.chainData();
    const selectionChains: Chain[] = proteinChains.length > 0
      ? proteinChains
      : [{
          name: 'ProteinA',
          label_asym_id: undefined,
          length: this.entryData().sequenceEnd,
          sequenceEnd: this.entryData().sequenceEnd,
          sequenceStart: 1,
        }];

    const selectionData = this.utilService.getSelectionData(regions, selectionChains);

    await this.pdbeMolstar.visual.select({
      data: selectionData,
      nonSelectedColor: '#707372',
      keepRepresentations: true,
    });

    await this.pdbeMolstar.visual.sequenceColor({
      data: selectionData,
    });
  }

  applyMolstarColoring(): void {
    if (!this.pdbeMolstar) {
      return;
    }

    resetColoring(this.pdbeMolstar);
    resetTooltips(this.pdbeMolstar);
    this.pdbeMolstar.visual.reset({ camera: true });
  }

  initMolstar(): void {
    this.structure = {
      model_url: this.entryData().bcifUrl,
      model_format: 'cif',
      binary: true,
    };

    const modelCreatedDate = new Date(this.entryData().modelCreatedDate);
    this.lastUpdateDate = `${modelCreatedDate.getDate()} ${modelCreatedDate.toLocaleString('default', {
      month: 'long',
    })} ${modelCreatedDate.getFullYear()}`;

    setTimeout(() => {
      this.handleMolstar(this.structure);
    }, 10);
  }

  async handleMolstar(structure: Structure): Promise<void> {
    const options = {
      customData: {
        url: structure.model_url,
        format: structure.model_format,
        binary: structure.binary,
      },
      subscribeEvents: false,
      bgColor: { r: 255, g: 255, b: 255 },
      alphafoldView: true,
      reactive: true,
      sequencePanel: true,
      hideCanvasControls: ['animation'],
      landscape: true,
      rightPanel: true,
      hideControls: false,
      validationAnnotation: true,
      granularity: 'residue',
    };

    if (this.pdbeMolstar) {
      this.pdbeMolstar.visual.update(options, true);
      this.applyMolstarColoring();
      return;
    }

    const ele = this.el.nativeElement.querySelector('.macromolecules-molstar-container');
    const eleSeq =
      this.el.nativeElement.querySelector('.macromolecules-sequencepanel');

    if (!ele) {
      return;
    }

    await this.molstarPluginService.loadPlugin();
    const pluginInstance = this.molstarPluginService.createInstance();
    this.pdbeMolstar = pluginInstance;
    const layout = [
          { target: ele, component: this.molstarPluginService.getClass().UIComponents.FullLayoutNoControlsUnlessExpanded },
          { target: eleSeq, component: this.molstarPluginService.getClass().UIComponents.SequenceView, props: { defaultMode: 'polymers'  }},
        ]

    this.pdbeMolstar.render(layout, options);

    this.pdbeMolstar.events.loadComplete.subscribe(async () => {
      this.molLoadComplete = true;
      this.applyMolstarColoring();
      await this.applyDefaultChainSelectionOnLoad();
    });
  }
}
